import { useState, useEffect } from 'react';
import { menuAPI } from '../services/api';

// Hook for fetching complete menu
export const useMenu = () => {
  const [menu, setMenu] = useState({
    categories: [],
    totalItems: 0,
    totalCategories: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        setError(null);
        const menuData = await menuAPI.getCompleteMenu();
        setMenu(menuData);
      } catch (err) {
        setError(err.message || 'Failed to fetch menu');
        console.error('Menu fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  const refreshMenu = async () => {
    try {
      setLoading(true);
      setError(null);
      const menuData = await menuAPI.getCompleteMenu();
      setMenu(menuData);
    } catch (err) {
      setError(err.message || 'Failed to refresh menu');
    } finally {
      setLoading(false);
    }
  };

  return { menu, loading, error, refreshMenu };
};

// Hook for fetching menu by category
export const useCategoryMenu = (categorySlug) => {
  const [categoryMenu, setCategoryMenu] = useState({
    data: [],
    count: 0,
    category: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategoryMenu = async () => {
      if (!categorySlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await menuAPI.getCategoryMenu(categorySlug);
        setCategoryMenu(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch category menu');
        console.error('Category menu fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryMenu();
  }, [categorySlug]);

  return { categoryMenu, loading, error };
};

// Hook for menu search
export const useMenuSearch = () => {
  const [searchResults, setSearchResults] = useState({
    items: [],
    count: 0,
    query: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchMenu = async (query, categoryId = null) => {
    if (!query.trim()) {
      setSearchResults({ items: [], count: 0, query: '' });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await menuAPI.searchItems(query, categoryId);
      setSearchResults(results);
    } catch (err) {
      setError(err.message || 'Search failed');
      console.error('Menu search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchResults({ items: [], count: 0, query: '' });
    setError(null);
  };

  return { searchResults, loading, error, searchMenu, clearSearch };
};

// Hook for fallback to static data
export const useMenuWithFallback = () => {
  const { menu, loading, error } = useMenu();
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    if (error) {
      // If API fails, we could fall back to static data
      setUsingFallback(true);
      console.warn('API unavailable, would fall back to static data');
    }
  }, [error]);

  return { 
    menu, 
    loading, 
    error, 
    usingFallback,
    isOnline: !error && !usingFallback 
  };
};